import styled from '@emotion/styled';
import { Button, ButtonProps } from 'antd';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { LocaleKeys } from '../../locale';
import { Paths, SecondaryPaths } from '../../types/common';
import { ESortOption, VideoSortOptions } from '../../types/video';

const Wrapper = styled('div')`
  padding: 40px 0 0 0;
  display: flex;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 948px) {
    gap: 20px;
    padding: 20px 0;
  }

  @media (max-width: 738px) {
    flex-direction: column;
    gap: 10px;
  }

  @media (max-width: 539px) {
    .button-wrapper {
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
    }
  }
`;

const LinksContainer = styled('div')`
  display: flex;
  gap: 4px;
`;

const LinkItem = styled(NavLink)`
  height: 50px;
  width: fit-content;
  padding-inline: 20px;
  border-radius: 25px;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  border: 1px solid transparent;
  transition:
    background-color 0.3s ease-out,
    color 0.3s ease-out,
    border-color 0.3s ease-out;

  :hover:not(.active) {
    color: ${({ theme }) => theme.COLORS.WHITE._600};
    border-color: ${({ theme }) => theme.COLORS.ACCENT._200};
  }

  &.active {
    color: ${({ theme }) => theme.COLORS.ACCENT._200};
    background-color: ${({ theme }) => theme.COLORS.BLACK._400};
  }

  @media (max-width: 948px) {
    height: 30px;
    font-size: 16px;
  }
`;

const PlayListButton = styled(Button)`
  height: 50px;
  border-radius: 25px;
  font-size: 20px;
  font-weight: 600;
  line-height: 100%;

  @media (max-width: 948px) {
    height: 30px;
    font-size: 16px;
  }
`;

const OperationsButtons = styled('div')`
  margin-right: 16px;
  display: flex;
  gap: 24px;
`;

const SelectStyled = styled(Select)`
  width: 220px;
  @media (max-width: 948px) {
    height: 30px;
    width: 180px;

    span {
      font-size: 16px;
    }
  }
`;

export const PageMenu: FC<{
  setPlayListModal: Dispatch<SetStateAction<boolean>>;
  setSortOption: Dispatch<SetStateAction<ESortOption>>;
  playlistEditMode?: boolean;
  handleSavePlaylist?: () => void;
  exitEditMode?: () => void;
  operationsButtonProps?: ButtonProps;
  sortOption: ESortOption;
  videoLink?: string;
  playListLink?: string;
  showNavigationLinks?: boolean;
  blockEditPlaylist?: boolean;
  createMode?: 'playlist' | 'playcourse';
}> = ({
  setPlayListModal,
  playlistEditMode,
  handleSavePlaylist,
  exitEditMode,
  operationsButtonProps,
  sortOption,
  setSortOption,
  videoLink,
  playListLink,
  showNavigationLinks = true,
  blockEditPlaylist,
  createMode,
}) => {
  const { t } = useTranslation([LocaleKeys.VIDEO]);
  const location = useLocation();
  const [isPlayListPage, setIsPlayListPage] = useState(false);

  useEffect(() => {
    if (
      location.pathname.includes(SecondaryPaths.PLAYLISTS) ||
      location.pathname.includes(SecondaryPaths.PLAYCOURSES)
    ) {
      setIsPlayListPage(true);
    } else {
      setIsPlayListPage(false);
    }
  }, [location]);

  const handleOpenPlayList = () => {
    setPlayListModal(true);
  };

  return (
    <Wrapper>
      {showNavigationLinks && (
        <Space size={10}>
          <LinksContainer>
            <LinkItem end to={videoLink ?? Paths.VIDEO}>
              {t('menu.video')}
            </LinkItem>
            {!playlistEditMode && (
              <LinkItem end to={playListLink ?? Paths.UPLOADED_VIDEOS_PLAYLIST}>
                {t('menu.playlist.title')}
              </LinkItem>
            )}
          </LinksContainer>
        </Space>
      )}
      <Space className="button-wrapper">
        {isPlayListPage && !playlistEditMode && !blockEditPlaylist && (
          <PlayListButton type="primary" onClick={handleOpenPlayList}>
            {createMode === 'playcourse'
              ? 'Create PlayCourse'
              : t('menu.playlist.create')}
          </PlayListButton>
        )}
        {playlistEditMode && (
          <OperationsButtons>
            <Button
              type="primary"
              onClick={handleSavePlaylist}
              size="large"
              shape="round"
              {...operationsButtonProps}
            >
              {t('buttons.save.common')}
            </Button>
            <Button
              onClick={exitEditMode}
              size="large"
              shape="round"
              {...operationsButtonProps}
            >
              {t('buttons.cancel')}
            </Button>
          </OperationsButtons>
        )}
        <SelectStyled
          value={sortOption}
          options={VideoSortOptions(t)}
          onChange={(value) => setSortOption(value as ESortOption)}
        />
      </Space>
    </Wrapper>
  );
};
